import { Fragment } from 'react'
import { FileText, NotebookText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import type { MeetingNoteRow } from '@/features/meetings/hooks/useMeetingNotesTableQuery'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
})

function formatDuration(seconds?: number | null) {
  if (seconds == null) return null
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs.toString().padStart(2, '0')}s`
}

type MeetingNoteCardProps = {
  note: MeetingNoteRow
}

export function MeetingNoteCard({ note }: MeetingNoteCardProps) {
  const { toTask } = useAppNavigate()

  const title =
    note.title?.trim() || note.summary?.split('\n')[0]?.trim() || 'View note'

  const eventDate = note.event_time ? new Date(note.event_time) : null
  const subtitleParts = [
    eventDate ? dateFormatter.format(eventDate) : null,
    eventDate ? timeFormatter.format(eventDate) : null,
    formatDuration(note.duration_seconds),
  ].filter((part): part is string => Boolean(part))

  const hasTranscript = note.has_transcript === true

  return (
    // Whole-card click affordance via the stretched-link pattern: the title
    // button's `after:absolute after:inset-0` overlay extends its hit area to
    // the whole card. The transcript control (relative z-10) stays above the
    // overlay and independently clickable.
    <Card className="relative flex-row items-center justify-between gap-2 px-5 py-5 transition-shadow hover:shadow-lg">
      <div className="flex items-start gap-4 min-w-0">
        <FileText
          size={22}
          strokeWidth={1.5}
          className="shrink-0 mt-0.5 text-primary"
        />
        <div className="flex flex-col gap-2 min-w-0">
          <button
            type="button"
            onClick={() => toTask(`/notes/${note.id}`)}
            className="cursor-pointer truncate text-left text-base font-normal text-primary-high-contrast after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded-sm">
            {title}
          </button>
          {subtitleParts.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
              {subtitleParts.map((part, index) => (
                <Fragment key={part}>
                  {index > 0 && <span className="text-xl">•</span>}
                  <span className="font-medium">{part}</span>
                </Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="relative z-10">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={!hasTranscript}
          onClick={() => toTask(`/transcripts/${note.id}`)}
          aria-label={
            hasTranscript ? 'Open transcript' : 'Transcript unavailable'
          }
          title={hasTranscript ? 'Open transcript' : 'Transcript unavailable'}>
          <NotebookText size={18} />
        </Button>
      </div>
    </Card>
  )
}
